/**
 * Restaurant Model Configuration
 * COMPLETE extraction of ALL assumption fields from the existing Restaurant model
 * 
 * Source: /server/restaurant/restaurant-api.js (defaultAssumptions)
 * 
 * FIELD COUNT: 45+ fields
 */

import type { ModelConfig } from '@/types/models';
import { standardResultTabs } from '@/config/resultTabs';

export const restaurantConfig: ModelConfig = {
  modelType: 'restaurant',
  apiEndpoint: '/api/restaurant',
  displayName: 'Restaurant',
  resultTabs: standardResultTabs,
  description: 'Dine-in restaurant model with seating capacity, table turns, and food/beverage mix.',
  icon: '🍽️',
  color: '#f97316',

  assumptionGroups: [
    {
      id: 'user-details',
      title: '👤 Your Details',
      fields: [
        { id: 'userEmail', label: 'Your Email (for updates)', type: 'email', placeholder: 'name@company.com' },
      ],
    },
    {
      id: 'core-revenue',
      title: '💰 Core Revenue Drivers',
      fields: [
        { id: 'avgCheckSize', label: 'Average Check Size ($)', type: 'number', defaultValue: 42, min: 0 },
        { id: 'seatingCapacity', label: 'Seating Capacity', type: 'number', defaultValue: 75, min: 1 },
        { id: 'tableTurnsPerDay', label: 'Table Turns per Day', type: 'number', defaultValue: 3.2, min: 0, step: 0.1 },
        { id: 'operatingDaysMonth', label: 'Operating Days per Month', type: 'number', defaultValue: 30, min: 1, max: 31 },
      ],
    },
    {
      id: 'utilization',
      title: '📈 Capacity Utilization by Year',
      fields: [
        { id: 'utilizationY1', label: 'Year 1 Utilization (%)', type: 'number', defaultValue: 65, min: 0, max: 100 },
        { id: 'utilizationY2', label: 'Year 2 Utilization (%)', type: 'number', defaultValue: 70, min: 0, max: 100 },
        { id: 'utilizationY3', label: 'Year 3 Utilization (%)', type: 'number', defaultValue: 75, min: 0, max: 100 },
        { id: 'utilizationY4', label: 'Year 4 Utilization (%)', type: 'number', defaultValue: 78, min: 0, max: 100 },
        { id: 'utilizationY5', label: 'Year 5 Utilization (%)', type: 'number', defaultValue: 80, min: 0, max: 100 },
      ],
    },
    {
      id: 'revenue-mix',
      title: '🍷 Revenue Mix',
      fields: [
        { id: 'foodRevenuePct', label: 'Food Revenue (%)', type: 'number', defaultValue: 70, min: 0, max: 100 },
        { id: 'beverageRevenuePct', label: 'Beverage Revenue (%)', type: 'number', defaultValue: 30, min: 0, max: 100 },
        { id: 'deliveryTakeoutPct', label: 'Delivery/Takeout (%)', type: 'number', defaultValue: 0, min: 0, max: 100 },
        { id: 'cateringEventsPct', label: 'Catering/Events (%)', type: 'number', defaultValue: 0, min: 0, max: 100 },
      ],
    },
    {
      id: 'annual-increases',
      title: '📊 Annual Increases',
      fields: [
        { id: 'annualAOVIncrease', label: 'Annual Menu Price Increase (%)', type: 'number', defaultValue: 3, min: 0 },
        { id: 'annualWageIncrease', label: 'Annual Wage Increase (%)', type: 'number', defaultValue: 4, min: 0 },
        { id: 'annualRentIncrease', label: 'Annual Rent Increase (%)', type: 'number', defaultValue: 3, min: 0 },
        { id: 'annualUtilityIncrease', label: 'Annual Utility Increase (%)', type: 'number', defaultValue: 5, min: 0 },
      ],
    },
    {
      id: 'cost-structure',
      title: '💵 Cost Structure',
      fields: [
        { id: 'foodCostPct', label: 'Food Cost (%)', type: 'number', defaultValue: 28, min: 0, max: 100 },
        { id: 'beverageCostPct', label: 'Beverage Cost (%)', type: 'number', defaultValue: 22, min: 0, max: 100 },
        { id: 'laborCostPct', label: 'Labor Cost (%)', type: 'number', defaultValue: 32, min: 0, max: 100 },
        { id: 'monthlyRent', label: 'Monthly Rent ($)', type: 'number', defaultValue: 12500, min: 0 },
        { id: 'utilitiesPct', label: 'Utilities (%)', type: 'number', defaultValue: 4, min: 0 },
      ],
    },
    {
      id: 'operating-expenses',
      title: '🔧 Operating Expenses',
      fields: [
        { id: 'salesMarketingPct', label: 'Marketing (%)', type: 'number', defaultValue: 3, min: 0 },
        { id: 'maintenancePct', label: 'Maintenance (%)', type: 'number', defaultValue: 2, min: 0 },
        { id: 'suppliesPct', label: 'Supplies (%)', type: 'number', defaultValue: 2, min: 0 },
        { id: 'wastagePct', label: 'Wastage (%)', type: 'number', defaultValue: 3, min: 0 },
        { id: 'gaPct', label: 'G&A (%)', type: 'number', defaultValue: 8, min: 0 },
        { id: 'monthlyTechCosts', label: 'Monthly Tech Costs ($)', type: 'number', defaultValue: 500, min: 0 },
        { id: 'annualInsurance', label: 'Annual Insurance ($)', type: 'number', defaultValue: 24000, min: 0 },
        { id: 'annualLicenses', label: 'Annual Licenses ($)', type: 'number', defaultValue: 5000, min: 0 },
      ],
    },
    {
      id: 'fixed-assets',
      title: '🏗️ Fixed Assets',
      fields: [
        { id: 'initialEquipmentValue', label: 'Kitchen Equipment ($)', type: 'number', defaultValue: 250000, min: 0 },
        { id: 'initialFurnitureValue', label: 'Furniture & Fixtures ($)', type: 'number', defaultValue: 100000, min: 0 },
        { id: 'initialDecorValue', label: 'Interior Decor ($)', type: 'number', defaultValue: 50000, min: 0 },
        { id: 'equipmentDepreciationRate', label: 'Equipment Depreciation (%)', type: 'number', defaultValue: 20, min: 0 },
        { id: 'furnitureDepreciationRate', label: 'Furniture Depreciation (%)', type: 'number', defaultValue: 15, min: 0 },
        { id: 'initialLaunchCosts', label: 'Initial Launch Costs ($)', type: 'number', defaultValue: 50000, min: 0 },
      ],
    },
    {
      id: 'debt-interest',
      title: '🏦 Debt & Interest',
      fields: [
        { id: 'initialDebtAmount', label: 'Initial Debt ($)', type: 'number', defaultValue: 400000, min: 0 },
        { id: 'annualInterestRate', label: 'Interest Rate (%)', type: 'number', defaultValue: 8, min: 0 },
        { id: 'debtRepaymentYears', label: 'Repayment Years', type: 'number', defaultValue: 8, min: 1 },
      ],
    },
    {
      id: 'working-capital',
      title: '💰 Working Capital',
      fields: [
        { id: 'daysInAccountsReceivable', label: 'Days in AR', type: 'number', defaultValue: 2, min: 0 },
        { id: 'daysInAccountsPayable', label: 'Days in AP', type: 'number', defaultValue: 30, min: 0 },
        { id: 'inventoryDays', label: 'Inventory Days', type: 'number', defaultValue: 7, min: 0 },
        { id: 'prepaidExpensesPct', label: 'Prepaid Expenses (%)', type: 'number', defaultValue: 5, min: 0 },
        { id: 'accruedExpensesPct', label: 'Accrued Expenses (%)', type: 'number', defaultValue: 8, min: 0 },
        { id: 'securityDepositMonths', label: 'Security Deposit (months)', type: 'number', defaultValue: 2, min: 0 },
        { id: 'minimumCashBalance', label: 'Minimum Cash ($)', type: 'number', defaultValue: 50000, min: 0 },
      ],
    },
    {
      id: 'capital-raising',
      title: '🚀 Capital Raising',
      fields: [
        { id: 'initialCapitalRaise', label: 'Initial Capital ($)', type: 'number', defaultValue: 500000, min: 0 },
        { id: 'year2CapitalRaise', label: 'Year 2 Capital ($)', type: 'number', defaultValue: 0, min: 0 },
        { id: 'year3CapitalRaise', label: 'Year 3 Capital ($)', type: 'number', defaultValue: 200000, min: 0 },
        { id: 'year4CapitalRaise', label: 'Year 4 Capital ($)', type: 'number', defaultValue: 0, min: 0 },
        { id: 'year5CapitalRaise', label: 'Year 5 Capital ($)', type: 'number', defaultValue: 0, min: 0 },
        { id: 'automaticCapitalRaise', label: 'Automatic Capital Raise', type: 'checkbox', defaultValue: true, helpText: 'Automatically raise capital when cash goes negative' },
      ],
    },
    {
      id: 'financial-tax',
      title: '🏛️ Financial & Tax',
      fields: [
        { id: 'corporateTaxRate', label: 'Corporate Tax Rate (%)', type: 'number', defaultValue: 25, min: 0, max: 60 },
        { id: 'maintenanceCapexPct', label: 'Maintenance CapEx (%)', type: 'number', defaultValue: 2, min: 0 },
      ],
    },
  ],

};

export const restaurantDefaultAssumptions = {
  avgCheckSize: 42,
  seatingCapacity: 75,
  tableTurnsPerDay: 3.2,
  operatingDaysMonth: 30,
  utilizationY1: 65,
  utilizationY2: 70,
  utilizationY3: 75,
  utilizationY4: 78,
  utilizationY5: 80,
  foodRevenuePct: 70,
  beverageRevenuePct: 30,
  annualAOVIncrease: 3,
  annualWageIncrease: 4,
  annualRentIncrease: 3,
  annualUtilityIncrease: 5,
  foodCostPct: 28,
  beverageCostPct: 22,
  laborCostPct: 32,
  monthlyRent: 12500,
  utilitiesPct: 4,
  salesMarketingPct: 3,
  maintenancePct: 2,
  suppliesPct: 2,
  wastagePct: 3,
  gaPct: 8,
  monthlyTechCosts: 500,
  deliveryTakeoutPct: 0,
  cateringEventsPct: 0,
  initialLaunchCosts: 50000,
  corporateTaxRate: 25,
  initialEquipmentValue: 250000,
  initialFurnitureValue: 100000,
  initialDecorValue: 50000,
  equipmentDepreciationRate: 20,
  furnitureDepreciationRate: 15,
  initialDebtAmount: 400000,
  annualInterestRate: 8,
  debtRepaymentYears: 8,
  daysInAccountsReceivable: 2,
  daysInAccountsPayable: 30,
  inventoryDays: 7,
  prepaidExpensesPct: 5,
  accruedExpensesPct: 8,
  securityDepositMonths: 2,
  minimumCashBalance: 50000,
  initialCapitalRaise: 500000,
  year2CapitalRaise: 0,
  year3CapitalRaise: 200000,
  year4CapitalRaise: 0,
  year5CapitalRaise: 0,
  automaticCapitalRaise: true,
  maintenanceCapexPct: 2,
  annualInsurance: 24000,
  annualLicenses: 5000,
  revenueMultiple: 1.2,
  ebitdaMultiple: 5,
  discountRate: 0.15,
  terminalGrowth: 0.02,
};
